import { styled, Switch } from "@mui/material";
import { actualCompletionTimeFormatter, ipDelayedStartFormatter, ipLongRunningFormatter, FailedStatusFormatter, SuccessStatusFormatter, programSlaDateTimeFormatter, projectActionIconsFormatter, SlaStatusFormatter, } from "./DataPipelineFormatter";

export const dropdownSxProps = {
    borderColor: 'light.main',
    borderWidth: '1px',
    borderStyle: 'solid',
    '&.MuiInputBase-root': {
        border: 'none'
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(113, 117, 143, 0.8) !important'
    },
    '& .MuiSelect-select': {
        fontSize: '14px',
        fontWeight: '500',
        color: '#F5F5F5 !important'
    },
    '& .MuiSelect-icon': {
        top: 'calc(50% - .7em)'
    }
};

export function convertTimeToNumber(time: string) {
    // Remove the colon and convert the string to a number
    const timeStr = time.replace(':', '');

    // Convert to a number, removing any leading zeros
    return Number(timeStr);
}

export function convertDayTime(input: string) {
    // Split the input into day and time parts
    const [dayStr, time] = input.split(', ');

    // Map days of the week to their respective numbers
    const daysMap: any = {
        sun: 0,
        mon: 1,
        tue: 2,
        wed: 3,
        thu: 4,
        fri: 5,
        sat: 6
    };

    // Get the numeric value for the day
    const day = daysMap[dayStr.toLowerCase()];

    // Convert the time part to a number without the colon
    const timeNum = Number(time.replace(':', ''));

    // Return the desired object
    return {
        day: day,
        time: timeNum
    };
}

export const transformPlatformId = (value: any[]): number[] => {
    const platformMap: { [key: string]: number } = {
        'Teradata': 1,
        'BODS': 2,
        'DDH': 3,
        'ADB': 4
    };
    return value.map(id => platformMap[id] || id).filter(Boolean);
};

export const transformCyclic = (value: any[]): number[] => {
    return value
        .map((id) => {
            if (id === 'Yes') return 1;
            if (id === 'No') return 0;
            return id;
        })
        .filter((x) => x !== undefined);
};

export const normalizeFilter = (filter: any): any => {
    let colFilterInfo = { ...filter };
    for (const [key, value] of Object.entries(filter)) {
        if (key === 'platformId') {
            colFilterInfo.platformId = transformPlatformId(value as any[]);
        } else if (key === 'priCyclic') {
            colFilterInfo.Cyclic = transformCyclic(value as any[]);
            delete colFilterInfo.priCyclic;
        }
    }
    return colFilterInfo;
};

const mapPlatformIdToName = (platformNum: number, useExtendedMap: boolean = true): string => {
    const baseMap: Record<number, string> = {
        1: 'Teradata',
        2: 'BODS',
        3: 'DDH'
    };

    const extendedMap: Record<number, string> = {
        ...baseMap,
        4: 'ADF',
        7: 'MDT',
        8: 'Windows Server',
        9: 'SAP BW',
        10: 'SQL Server Agent (NewsPage)',
        11: 'ADB',
        12: 'IICS'
    };

    const platformMap = useExtendedMap ? extendedMap : baseMap;

    return platformMap[platformNum] || 'ADB';
};

export const transformFilterOptions = (filterOptions: any, useExtendedMap: boolean = true): any => {
    const result: any = { jobName: [], platformId: [] };
    const cyclicMap: { [key: number]: string } = { 1: 'Yes', 0: 'No' };

    for (const [key, raw] of Object.entries(filterOptions)) {
        const value = raw as any;
        if (key === 'Job_Name' || key === 'jobName') {
            result.jobName = value;
        } else if (key === 'Scheduler_Name' && value?.length) {
            result.schedulerName = value;
        } else if (key === 'Platform_Id' || key === 'platformId') {
            result.platformId = (value ?? []).map((platformNum: number) =>
                mapPlatformIdToName(platformNum, useExtendedMap));
        } else if (key === 'Cyclic' && value?.length) {
            result.priCyclic = (value as any[]).map(num => cyclicMap[num]).filter(Boolean);
        }
    }
    return result;
};

export const parseFilter = (filterInfo: any, name?: string) => {
    // Call only filterable columns and reset the filtering in case of wrong/empty column
    const parsedFilter: any = {};
    for (const key of Object.keys(filterInfo)) {
        if (key !== name && filterInfo[key]?.length) {
            parsedFilter[key] = filterInfo[key];
        }
    }
    return parsedFilter;
};

export const AntSwitch = styled(Switch)(({ theme }) => ({
    width: '32px',
    height: '18px',
    padding: 0,
    display: 'flex',
    '&:active': {
        '& .MuiSwitch-thumb': {
            width: 15,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(9px)',
        },
    },
    '& .MuiSwitch-switchBase': {
        padding: 3,
        color: 'rgba(0, 30, 62, 1)',
        '&.Mui-checked': {
            transform: 'translateX(12px)',
            color: 'rgba(0, 30, 62, 1)',

            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#1890ff',
                ...theme.applyStyles('dark', {
                    backgroundColor: '#177ddc',
                }),
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
        width: 12,
        height: 12,
        borderRadius: 6,
        transition: theme.transitions.create(['width'], {
            duration: 200,
        }),
    },
    '& .MuiSwitch-track': {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: 'rgba(113, 117, 143, 1)',
        boxSizing: 'border-box',
        ...theme.applyStyles('dark', {
            backgroundColor: 'rgba(255,255,255,.35)',
        }),
    },
}));

export const getInputCustomCommonSx = (
    colors: any,
    borderColor?: string,
    iconColor?: string,
    disabledIconColor?: string
) => ({
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${borderColor ?? colors.input.border} !important`,
        border: `1px solid ${borderColor ?? colors.input.border}`,
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: `${borderColor ?? colors.input.border} !important`,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${borderColor ?? colors.input.border} !important`,
    },

    '& .MuiAutocomplete-popupIndicator': {
        color: `${iconColor ?? colors.input.text} !important`,

        '& svg': {
            color: `${iconColor ?? colors.input.text} !important`,
            fill: `${iconColor ?? colors.input.text} !important`,
            stroke: `${iconColor ?? colors.input.text} !important`,
        },

        '& svg path': {
            fill: `${iconColor ?? colors.input.text} !important`,
            stroke: `${iconColor ?? colors.input.text} !important`,
        },
    },

    '& .MuiAutocomplete-clearIndicator': {
        color: `${iconColor ?? colors.input.text} !important`,

        '& svg': {
            color: `${iconColor ?? colors.input.text} !important`,
            fill: `${iconColor ?? colors.input.text} !important`,
            stroke: `${iconColor ?? colors.input.text} !important`,
        },

        '& svg path': {
            fill: `${iconColor ?? colors.input.text} !important`,
            stroke: `${iconColor ?? colors.input.text} !important`,
        },
    },
    '& .MuiAutocomplete-clearIndicator svg path': {
        fill: 'currentColor !important'
    },
    '& .MuiAutocomplete-popupIndicator.Mui-disabled': {
        color: `${disabledIconColor ?? colors.DropdownV2?.mutedText} !important`
    },
    '& .MuiAutocomplete-popupIndicator.Mui-disabled svg': {
        color: `${disabledIconColor ?? colors.DropdownV2?.mutedText} !important`,
        fill: 'currentColor !important'
    },
    '& .MuiAutocomplete-popupIndicator.Mui-disabled svg path': {
        fill: 'currentColor !important'
    },
    '& .MuiAutocomplete-clearIndicator.Mui-disabled': {
        color: `${disabledIconColor ?? colors.DropdownV2?.mutedText} !important`
    }
});


export const getAutocompleteCommonComponentsProps = (colors: any) => ({
    paper: {
        sx: {
            bgcolor: `${colors.dropdown.menuItemBackground} !important`,
            color: `${colors.dropdown.menuItemText} !important`
        }
    },
    popper: {
        sx: {
            '& .MuiAutocomplete-option': {
                bgcolor: `${colors.dropdown.menuItemBackground} !important`,
                color: `${colors.dropdown.menuItemText} !important`,
                '&.Mui-selected': {
                    backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
                    color: `${colors.dropdown.menuItemHoverText} !important`
                },
                '&.Mui-selected:hover': {
                    backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                    color: `${colors.dropdown.menuItemHoverText} !important`
                },
                '&:hover': {
                    backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                    color: `${colors.dropdown.menuItemHoverText} !important`
                }
            }
        }
    }
});

export const getAutocompleteNewComponentsProps = (colors: any) => ({
    paper: {
        sx: {
            mt: 1,
            overflow: 'hidden',
            boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.12)',

            '& .MuiAutocomplete-listbox': {
                p: 0,
                backgroundColor: `${colors.DropdownV2?.menuItemBackground} !important`,
                color: `${colors.DropdownV2?.menuItemText} !important`
            },

            '& .MuiAutocomplete-option': {
                color: `${colors.DropdownV2?.menuItemText} !important`,
                backgroundColor: `${colors.DropdownV2?.menuItemBackground} !important`,
                padding: '10px 16px',
                position: 'relative',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                opacity: 1
            },

            '& .MuiAutocomplete-noOptions': {
                backgroundColor: `${colors.DropdownV2?.menuItemBackground} !important`,
                color: `${colors.DropdownV2?.menuItemText} !important`,
                padding: '10px 16px',
                minHeight: '50px',
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box'
            },

            '& .MuiAutocomplete-option:hover': {
                backgroundColor: `${colors.DropdownV2?.menuItemHover} !important`,
                color: `${colors.DropdownV2?.menuItemText} !important`
            },

            '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option.Mui-focusVisible': {
                backgroundColor: `${colors.DropdownV2?.menuItemHover} !important`,
                color: `${colors.DropdownV2?.menuItemText} !important`
            },

            '& .MuiAutocomplete-option[aria-selected="true"]': {
                backgroundColor: `${colors.DropdownV2?.menuItemSelectedBg} !important`,
                color: `${colors.DropdownV2?.menuItemSelectedText} !important`,
                paddingRight: '40px',
                opacity: 1,

                '&::after': {
                    content: '"✓"',
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '18px',
                    fontWeight: 600,
                    lineHeight: 1,
                    color: `${colors.DropdownV2?.tickColor} !important`
                }
            },

            '& .MuiAutocomplete-option[aria-selected="true"]:hover, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused, & .MuiAutocomplete-option[aria-selected="true"].Mui-focusVisible': {
                backgroundColor: `${colors.DropdownV2?.menuItemSelectedBg} !important`,
                color: `${colors.DropdownV2?.menuItemSelectedText} !important`,
                opacity: 1
            },

            '& .MuiAutocomplete-option .MuiTypography-root': {
                color: `${colors.DropdownV2?.menuItemText} !important`
            },

            '& .MuiAutocomplete-option:hover .MuiTypography-root, & .MuiAutocomplete-option.Mui-focused .MuiTypography-root, & .MuiAutocomplete-option.Mui-focusVisible .MuiTypography-root': {
                color: `${colors.DropdownV2?.menuItemText} !important`
            },

            '& .MuiAutocomplete-option[aria-selected="true"] .MuiTypography-root, & .MuiAutocomplete-option[aria-selected="true"]:hover .MuiTypography-root, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused .MuiTypography-root': {
                color: `${colors.DropdownV2?.menuItemSelectedText} !important`
            }
        }
    },

    popper: {
        sx: {
            '& .MuiAutocomplete-option': {
                backgroundColor: `${colors.DropdownV2?.menuItemBackground} !important`,
                color: `${colors.DropdownV2?.menuItemText} !important`,
                position: 'relative',

                '&:hover': {
                    backgroundColor: `${colors.DropdownV2?.menuItemHover} !important`,
                    color: `${colors.DropdownV2?.menuItemText} !important`
                },

                '&.Mui-focused, &.Mui-focusVisible': {
                    backgroundColor: `${colors.DropdownV2?.menuItemHover} !important`,
                    color: `${colors.DropdownV2?.menuItemText} !important`
                },

                '&[aria-selected="true"]': {
                    backgroundColor: `${colors.DropdownV2?.menuItemSelectedBg} !important`,
                    color: `${colors.DropdownV2?.menuItemSelectedText} !important`,
                    paddingRight: '40px',

                    '&::after': {
                        content: '"✓"',
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '18px',
                        fontWeight: 600,
                        lineHeight: 1,
                        color: `${colors.DropdownV2?.tickColor} !important`
                    }
                },

                '&[aria-selected="true"]:hover, &[aria-selected="true"].Mui-focused, &[aria-selected="true"].Mui-focusVisible': {
                    backgroundColor: `${colors.DropdownV2?.menuItemSelectedBg} !important`,
                    color: `${colors.DropdownV2?.menuItemSelectedText} !important`
                }
            }
        }
    }
});

export const getCommonCustomTextFieldProps = (colors: any) => ({
    InputLabelProps: {
        sx: {
            '&&': { color: `${colors.input.text} !important` },
            '&.Mui-focused': { color: `${colors.input.text} !important` },
            '&.MuiInputLabel-shrink': { color: `${colors.input.text} !important` }
        }
    },
    InputProps: {
        sx: {
            '&&': { color: `${colors.input.text} !important` },
            '& input': { color: `${colors.input.text} !important` }
        }
    },
    sx: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: `${colors.input.border} !important` },
            '&:hover fieldset': { borderColor: `${colors.input.borderFocus} !important` },
            '&.Mui-focused fieldset': { borderColor: `${colors.input.borderFocus} !important` }
        }
    }
});

export const getBackgroundSx = (colors: any) => ({
    backgroundColor: `${colors.search.background} !important`
});

export const getSearchInputLabelProps = (colors: any) => ({
    sx: {
        backgroundColor: `${colors.search.background} !important`
    }
});

export const searchInputSx = {
    '& .MuiInputBase-input': {
        padding: '10px',
        paddingLeft: 0
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'dark.dark',
        borderWidth: '0px',
        borderStyle: 'solid'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'dark.dark',
        borderWidth: '0px'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'dark.dark',
        borderWidth: '0px'
    }
};

const calculateRdgHeight = (rowsLength: number): number => {
    if (rowsLength === 1) return rowsLength * 80;
    else if (rowsLength > 1 && rowsLength <= 3) return rowsLength * 65;
    else if (rowsLength > 3 && rowsLength <= 5) return rowsLength * 55;
    else if (rowsLength > 5 && rowsLength <= 10) return rowsLength * 50;
    else if (rowsLength > 10 && rowsLength <= 15) return rowsLength * 40;
    else return rowsLength * 30;
};

export const setRdgTableHeight = (
    tableId: string,
    rowsLength: number
): void => {
    let table = document.getElementById(tableId);
    if (table) {
        const rdgElement = table.querySelector('.rdg');
        if (rdgElement instanceof HTMLElement) {
            const height = calculateRdgHeight(rowsLength);
            rdgElement.style.height = `${height}px`;
        }
    }
};

// Helper function to clear session storage
export const clearSessionData = () => {
    sessionStorage.removeItem('executiveTemplateTab');
    sessionStorage.removeItem('executiveTemplateDashboard');
    sessionStorage.removeItem('executiveTemplateWorkflow');
    sessionStorage.removeItem('executiveTemplateHops');
    sessionStorage.removeItem('executiveTemplateSla'); // Also clear SLA data
};

export const getProjectCommonColumns = ({
    isAllowed,
    handleAction,
}: {
    isAllowed: boolean;
    handleAction: (module: string, action: string, row: any, e: any) => void;
}) => {
    return [
        {
            key: "IP_Delayed_Start",
            name: "Delayed Start",
            formatter: ipDelayedStartFormatter(),
            minWidth: 150,
        },
        {
            key: "IP_Long_Running",
            name: "Long Running",
            formatter: ipLongRunningFormatter(),
            minWidth: 150,
        },
        {
            key: "Failed",
            name: "Failed",
            formatter: FailedStatusFormatter,
            minWidth: 150,
        },
        {
            key: "Success",
            name: "Success",
            formatter: SuccessStatusFormatter,
            minWidth: 150,
        },
        {
            key: "SLA_Status",
            name: "SLA Status",
            formatter: SlaStatusFormatter,
            minWidth: 200,
        },
        {
            key: "SLA_Time",
            name: "SLA Time (CST)",
            formatter: programSlaDateTimeFormatter(),
            minWidth: 150,
        },
        {
            key: "Actual_Completion_Time",
            name: "Actual Completion Time (CST)",
            formatter: actualCompletionTimeFormatter(),
            minWidth: 150,
        },
        {
            key: "action",
            name: "Action",
            resizable: false,
            minWidth: 100,
            formatter: projectActionIconsFormatter(isAllowed, handleAction),
        },
    ];
};

export const getProjectExportColumns = () => {
    return [
        'Sectors',
        'Project',
        'Delayed Start',
        'Long Running',
        'Failed',
        'Success',
        'SLA Status',
        'SLA Time (CST)',
        'Actual Completion Time (CST)'
    ];
};

export const getProjectExportConfig = ({
    filterInfo,
    currentFilterType,
    stDate,
    edDate,
    sortingInfo,
    sectorId,
    projName,
    programID,
    programName
}: any) => {
    return {
        id: programID,
        page: 1,
        pageSize: 10000,
        name: programName,
        exportColumns: getProjectExportColumns(),
        formatType: "EXCEL",
        currentFilterType,
        filterInfo,
        startDate: stDate,
        endDate: edDate,
        sortingInfo,
        sectorId,
        projName
    };
};

export const getPaginationSlice = ({
    list,
    newPage,
    pageSize
}: {
    list: any[];
    newPage: number;
    pageSize: number;
}) => {
    const startIndex = Number(pageSize) * Number(newPage) - Number(pageSize);
    const endIndex = Number(pageSize) * Number(newPage) - Number(pageSize) + Number(pageSize);

    return list.slice(startIndex, endIndex);
};

// Converts job status to display label
export const getStatusLabel = (status: string, depStatus?: string): string => {
    if (status === 'Complete') return 'Success';
    if (status === 'Error' || status === 'Exceptions') return 'Fail';
    if (status === 'Processing') return 'In Progress';
    if (status) return status;
    if (depStatus) return depStatus;
    return '--';
};

// Converts scheduler display name based on platform and scheduler type
export const getSchedulerDisplayName = (row: any): string => {
    if (!row) return '--';
    const { platformId, schedulerName } = row;

    switch (platformId) {
        case 1:
            if (schedulerName === 'control-m') return 'Control-M';
            if (schedulerName === 'hwa') return 'HWA';
            return schedulerName || '--';
        case 2:
            return (schedulerName === 'control-m-pgt' || schedulerName === 'Control-M-PGT')
                ? 'Control-M-PGT'
                : (schedulerName || '--');
        case 3:
            return schedulerName === 'NATIVE' ? 'Native' : (schedulerName || '--');
        case 4:
        case 7:
        case 8:
        case 9:
        case 10:
        case 11:
        case 12:
            return schedulerName || '--';
        default:
            return '--';
    }
};

// Converts Scheduler column value to display label
export const getSchedulerLabel = (sched: string): string => {
    if (!sched) return '--';
    const schedulerMap: { [key: string]: string } = {
        'HWA': 'HWA',
        'Control-M-PGT': 'Control-M-PGT',
        'Control-M': 'Control-M',
        'NATIVE': 'Native',
        'CUSTOM': 'Custom',
        'MDT': 'MDT',
        'ADF': 'ADF',
        'Windows Task Schedule': 'Windows Task Scheduler',
        'SAP BW': 'SAP BW',
        'SQL Server': 'SQL Server',
        'ADB': 'ADB',
        'IICS': 'IICS'
    };
    return schedulerMap[sched] || '--';
};

// Converts cyclic value to display label
export const getCyclicLabel = (cyclic: string, scheduler?: string): string => {
    if (cyclic === '0' || !cyclic || scheduler === 'NATIVE') return 'No';
    if (cyclic === '-') return '--';
    return 'Yes';
};

// Formats schedule info by removing redundant text

export const formatScheduleInfo = (scheduleInfo: string): string => {
    if (!scheduleInfo) return '';
    return scheduleInfo.replace('At 0 minutes past the hour, ', '');
};

export const getDisabledInputSx = () => ({
    '& .MuiOutlinedInput-root.Mui-disabled': {
        backgroundColor: '#71758F0D !important',
        color: '#71758F !important',
        '& fieldset': {
            borderColor: '#e0e0e0 !important',
        }
    },
    '& .MuiOutlinedInput-root.Mui-disabled:hover': {
        '& fieldset': {
            borderColor: '#e0e0e0 !important',
        }
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input': {
        color: '#71758F !important',
        WebkitTextFillColor: '#71758F !important',
    },
    '& .MuiFormLabel-root.Mui-disabled': {
        color: '#71758F !important',
    }
});

export const getDropdownDisabledTextFieldSx = () => ({
    '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input': {
        WebkitTextFillColor: '#71758F !important',
        color: '#71758F !important',
    },
    '& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled': {
        color: '#71758F !important',
    },
});

export const getDropdownCustomSx = (colors: any) => ({
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${colors.inputNew.inputBorder} !important`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: `${colors.inputNew.inputBorderFocus} !important`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${colors.inputNew.inputBorderFocus} !important`,
    },
    '&.Mui-disabled': {
        backgroundColor: '#71758F0D !important',
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0 !important',
    },
    '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0 !important',
    },
    '& .MuiSelect-select.Mui-disabled': {
        color: '#71758F !important',
        WebkitTextFillColor: '#71758F !important',
    },
    '& .MuiSvgIcon-root': {
        color: '#71758F !important',
    },
    '& .Mui-disabled .MuiSvgIcon-root': {
        color: '#71758F !important',
    },
});

export const getDisabledSelectSx = () => ({
    '&.Mui-disabled': {
        backgroundColor: '#71758F0D !important',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0 !important',
    },
    '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0 !important',
    },
    '& .MuiSelect-select.Mui-disabled': {
        color: '#71758F !important',
        WebkitTextFillColor: '#71758F !important',
    },
    '& .MuiSvgIcon-root': {
        color: '#71758F !important',
    },
    '& .Mui-disabled .MuiSvgIcon-root': {
        color: '#71758F !important',
    },
});